/**
 * DashboardLayout — Haupt-Layout mit 3-Spalten-System (WinValue-Style)
 * 
 * LAYOUT:
 * - Header (fest oben, 56px) mit Hamburger-Icon
 * - Darunter: [Hauptmenü] [Prozessmenü (optional)] [Inhalt]
 * 
 * Wenn keine Akte offen:  [Hauptmenü 200px] [Inhalt flex-1]
 * Wenn Akte offen:        [Hauptmenü 200px] [Prozessmenü 220px] [Inhalt flex-1]
 * 
 * Hamburger zugeklappt:   [Schmale Sidebar 60px mit Icons] [ggf. Prozessmenü] [Inhalt]
 * Hamburger aufgeklappt:  [Vollständiges Hauptmenü 200px] [ggf. Prozessmenü] [Inhalt]
 * 
 * WICHTIG: Hauptmenü wird NIEMALS durch Prozessmenü ersetzt.
 * Beide sind gleichzeitig sichtbar (außer auf Mobile).
 */

import { useApp } from '@/contexts/AppContext';
import { useEffect } from 'react';
import { useLocation } from 'wouter';
import Header from './Header';
import Sidebar from './Sidebar';
import AktenProzessSpalte from './AktenProzessSpalte';
import MobileNav from './MobileNav';
import ForderungsmanagerDrawer from './ForderungsmanagerDrawer';
import ForderungsTagesbriefing from './ForderungsTagesbriefing';
import type { ReactNode } from 'react';

interface DashboardLayoutProps {
  children: ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    activeAkte,
    closeAkte,
    forderungsmanagerOpen,
    setForderungsmanagerOpen,
    forderungsAkteId,
  } = useApp();
  const [location] = useLocation();
  const isCalendar = location === '/kalender';

  // ===== ROBUSTER ROUTE-WATCHER =====
  // 1) Schließt das Prozessmenü wenn Route nicht mehr /akten/[betrieb]/[id]/... ist
  // 2) Schließt die Mobile-Sidebar bei Routenwechsel automatisch
  //    WICHTIG: sidebarCollapsed NICHT im Dependency-Array, sonst kann die Sidebar
  //    auf Mobile nie geöffnet werden (toggleSidebar → useEffect → sofort wieder zu)
  useEffect(() => {
    // Mobile-Sidebar schließen bei Navigation (nur bei Routenwechsel)
    if (window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
    // Prozessmenü schließen wenn nicht mehr auf Akten-Detail
    if (!activeAkte) return;
    const isAkteRoute = /^\/akten\/[^/]+\/[^/]+/.test(location);
    if (!isAkteRoute) {
      closeAkte();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setSidebarCollapsed]);

  const sidebarWidth = sidebarCollapsed ? 60 : 200;

  return (
    <div className="min-h-screen bg-[#EAECEF]">
      <Header />

      {/* ===== DESKTOP LAYOUT ===== */}
      <div className={`hidden md:flex pt-14 ${isCalendar ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
        {/* Spalte 1: Hauptmenü-Sidebar — IMMER sichtbar */}
        <aside
          className="fixed top-14 left-0 bottom-0 bg-[#1F2937] border-r border-[#374151] overflow-hidden transition-all duration-200 ease-in-out flex flex-col z-20"
          style={{ width: sidebarWidth }}
        >
          <Sidebar collapsed={sidebarCollapsed} />
        </aside>

        {/* Spalte 2: Prozess-Spalte — nur wenn Akte geöffnet */}
        {activeAkte && (
          <aside
            className="fixed top-14 bottom-0 bg-white border-r border-gray-200 overflow-hidden flex flex-col z-10 transition-all duration-200 ease-in-out"
            style={{ left: sidebarWidth, width: 220 }}
          >
            <AktenProzessSpalte />
          </aside>
        )}

        {/* Spalte 3: Inhaltsbereich */}
        <main
          className="flex-1 overflow-auto transition-all duration-200 ease-in-out"
          style={{ marginLeft: sidebarWidth + (activeAkte ? 220 : 0) }}
        >
          {children}
        </main>
      </div>

      {/* ===== MOBILE LAYOUT ===== */}
      <div className="md:hidden pt-14 min-h-screen">
        {/* Mobile Sidebar Overlay */}
        {!sidebarCollapsed && (
          <>
            <div
              className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
              onClick={() => setSidebarCollapsed(true)}
            />
            <aside className="fixed top-14 left-0 bottom-0 z-40 w-[260px] bg-[#1F2937] overflow-auto shadow-xl border-r border-[#374151]">
              <Sidebar collapsed={false} />
            </aside>
          </>
        )}

        {/* Mobile Prozess-Leiste (horizontal, oben) wenn Akte offen */}
        {activeAkte && (
          <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 overflow-x-auto">
            <AktenProzessSpalte mobile />
          </div>
        )}

        {/* Mobile Content */}
        <main className="overflow-auto pb-24">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileNav />

      {/* Forderungsmanager — globaler Drawer, von überall öffenbar */}
      <ForderungsmanagerDrawer
        open={forderungsmanagerOpen}
        onOpenChange={setForderungsmanagerOpen}
        akteId={forderungsAkteId ?? undefined}
      />

      {/* Tages-Assistent Forderungen — globales Popup, öffnet einmal täglich ab 09:00 */}
      <ForderungsTagesbriefing />
    </div>
  );
}
